import * as cheerio from 'cheerio/slim';
import escapeRegExp from 'lodash.escaperegexp';
const MATURE_FLAG_PATHS = ['/tag/m', '/series/m'];
const MATURE_FLAG_SLUGS = ['m', 'mature'];
const isNonEmptyArray = (arr) => Array.isArray(arr) && arr.length > 0;
/**
 * Given an array of ContentRichTerms and optional arrays of paths and slugs,
 * return true * if any of the terms match the terms to check. If no paths or
 * slugs are provided, we will check to see if any rich term paths match those of
 * MATURE_FLAGS_PATH or rich term display slugs match those of
 * MATURE_FLAGS_SLUGS. (We check both to account for user error in setting the
 * tags in the CMS.)
 */
export const isFlaggedMature = (richTerms, pathsArr, slugsArr) => {
    if (!isNonEmptyArray(richTerms)) {
        return false;
    }
    let paths = pathsArr ?? MATURE_FLAG_PATHS;
    let slugs = slugsArr ?? [];
    if (!isNonEmptyArray(pathsArr)) {
        paths = MATURE_FLAG_PATHS;
        // We set slugs to the default *only* if we have overriden pathsArr
        if (!slugsArr || !Array.isArray(slugsArr)) {
            slugs = MATURE_FLAG_SLUGS;
        }
    }
    return richTerms.some((term) => !Object.hasOwnProperty.call(term, '$ref') &&
        (paths.includes(term.path ?? '') || slugs.includes(term.slug ?? '')));
};
export const stripHtml = (input) => {
    if (input === undefined) {
        return null;
    }
    // If the input contains block tags, stripping the HTML will result in their text contents
    // being run together with no space between them, so just add a space between any pair of
    // closing tag and opening tag before stripping.
    const tags = ['p', 'h\\d', 'ol', 'ul', 'li', 'div', 'blockquote', 'aside', 'figure'].join('|');
    const re = new RegExp('(</(' + tags + ')>)(<(' + tags + '))', 'g');
    input = input.replace(re, '$1 $3');
    return cheerio.load(input).text();
};
const GALLERY_PICTURE_WORD_EQUIVALENT = 100;
// Starting in October 2020, Google began flagging pages using AdManager or
// AdX that "do not contain content"--things like running an article made up
// of one or two Instagram embeds. This is an optional check to make sure that
// the word count is above a configurable threshold. We treat certain page
// types--e.g. stock quotes--as presumptively shallow, and then ignore items
// that do not have bodyTml. Pages which are not automatically generated but
// maintained as static pages should be demonetized at the page template level.
//
// We will treat photos, hub pages, and rich terms as presumptively "deep" for
// the time being. Video pages will be demonetized if they don't have
// a sufficient amount of introductory content. These behaviors are subject to
// change based on further discovery about what signals Google is using to
// detect pages they think are abusive.
export const isShallowContent = (item, shallowConfig) => {
    if (!(shallowConfig.enabled ?? false))
        return false;
    // Some pages are presumptively shallow:
    // * Stock ticker pages
    // * ContentUserProfiles (author pages)
    // * Team pages
    // * Player pages
    // Game pages will be treated as standard articles; if there's enough
    // content in the bodyTml, we should not treat them as shallow.
    if (item.objectType === 'ContentUserProfile' ||
        item.objectType === 'ContentPlayer' ||
        (item.objectType === 'ContentOrganization' && item.extension === 'ticker') ||
        (item.objectType === 'ContentArticle' && item.extension === 'si_team')) {
        return true;
    }
    const SAFE_TYPES = ['ContentHubPage', 'ContentPhoto', 'ContentRichTerm'];
    if (SAFE_TYPES.includes(item.objectType ?? '')) {
        return false;
    }
    if (item.bodyTml == null || item.bodyTml === '') {
        return true;
    }
    // Let's look for inline galleries.
    let totalGallerySize = 0;
    // Initial quick check before more expensive regex.
    if (item.bodyTml.includes('tml-collection') && item.tmlEmbeds) {
        // Pull out gallery indices from the TML -- note that we're doing tihs
        // rather than looking for `ContentGallery` embeds because embed type
        // of inline is not an item-level property!
        const matches = item.bodyTml.matchAll(/div tml-collection="([a-fi0-9]+)"[^>]*tml-render-layout="inline"/g);
        // Now iterate through and tally up the embedded images for each
        // inline gallery.
        for (const m of matches) {
            const galleryId = m[1];
            item.tmlEmbeds.forEach((embedItem) => {
                if (embedItem.id === galleryId && embedItem.items) {
                    totalGallerySize += embedItem.items.length;
                }
            });
        }
    }
    let wordCount = 0;
    // Initial quick check before more expensive regex.
    if (item.bodyTml.includes('tml-external-provider')) {
        // Adds "weighting" i.e. a number equal to the number of words an embed
        // is "worth" Key is tml-embed-src value to match on and value is the
        // number of words to assign the embed
        const embedWeights = { 'mvn://numbrix': 250 };
        const pattern = Object.keys(embedWeights).map(escapeRegExp).join('|');
        const regex = new RegExp(`tml-embed-src="(${pattern})[^"]+"`, 'g');
        for (const [_, src] of item.bodyTml.matchAll(regex)) {
            wordCount += embedWeights[src];
        }
    }
    wordCount += stripHtml(item.bodyTml)?.trim().split(/\s+/).length ?? 0;
    // phoenix version allowed a number < undefined comparison, which is always
    // false. So we're using -Infinity threshold to mimic that behavior
    return (wordCount + totalGallerySize * GALLERY_PICTURE_WORD_EQUIVALENT <
        (shallowConfig.threshold ?? -Infinity));
};
//# sourceMappingURL=util.js.map